import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { ACTION_HISTORY_QUERY_POLICY } from 'api/queryPolicies'
import { cacheQuestPreservingPendingOptimisticAnswers } from 'state/pendingOptimisticAnswers'
import { questForCacheFromSummaryRow } from 'lib/historySummaryRow'

type ResponseData = {
    actions: any[]
    has_more?: boolean
}

// The sidebar's Chats list loads further pages as it scrolls, so its first page
// only has to fill the panel. Each row embeds a full quest, so the server default
// of 50 made this boot request close to 1MB.
export const PARTICIPATED_HISTORY_PAGE_SIZE = 20

type Options = {
    // Participated chats: threads you're a participant of, ordered by their
    // latest answer (which each entry carries for preview/permalink).
    participated?: boolean
}

export default function useGetHistory(enabled: boolean = true, options: Options = {}) {
    const participated = !!options.participated
    const emptyResult = {
        actions: [],
    }

    // Adjust the `enabled` flag based on the filter condition.
    // This ensures hooks are called in the same order every time.
    const shouldFetch = enabled

    // This must always be called with enabled flag controlling the fetch, to keep hook order consistent :(
    const result = useInfiniteQuery({
        queryKey: participated ? ['actions', 'participated'] : ['actions'],
        queryFn: async ({ pageParam = 1 }) => {
            const data: ResponseData = await api.getHistory(
                participated
                    ? {
                          page: pageParam,
                          participated: true,
                          per_page: PARTICIPATED_HISTORY_PAGE_SIZE,
                          // Root posts arrive as a summary (author and text): the
                          // full post was most of the page.
                          parent_payload: 'summary',
                      }
                    : { page: pageParam },
            )
            data.actions.forEach(action => {
                if (!action?.quest) return
                if (!participated) {
                    cacheQuestPreservingPendingOptimisticAnswers(action.quest, {
                        source: 'useGetHistory.query',
                    })
                    return
                }
                const quest = questForCacheFromSummaryRow(action.quest)
                if (quest) {
                    cacheQuestPreservingPendingOptimisticAnswers(quest, {
                        source: 'useGetHistory.query',
                    })
                }
            })
            return data
        },
        enabled: shouldFetch,
        staleTime: ACTION_HISTORY_QUERY_POLICY.staleTime,
        gcTime: ACTION_HISTORY_QUERY_POLICY.gcTime,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => (lastPage.has_more ? pages.length + 1 : undefined),
    })

    // Use the `enabled` flag to conditionally manipulate the result or return early.
    if (!shouldFetch) {
        return emptyResult
    }

    const actions = result.data?.pages.flatMap(page => page.actions) || []

    return {
        ...result,
        actions,
    }
}
